import { useState } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  IconButton,
  Collapse,
  TextField,
  Chip,
  alpha,
} from '@mui/material';
import {
  DragIndicator,
  ExpandMore,
  ExpandLess,
  Delete,
  CalendarToday,
  Add,
  Repeat,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TodoItem as TodoItemType } from '@/types/todolist';
import { SubtaskRow } from './SubtaskRow';
import {
  formatDueDate,
  isOverdue,
  isDueToday,
  getSubtaskCompletedCount,
  PRIORITY_COLORS,
} from '@/types/todolist';
import { brandColors } from '@/theme/colors';

type TodoItemProps = {
  item: TodoItemType;
  canEdit: boolean;
  onToggle: (itemId: string) => void;
  onUpdate: (itemId: string, updates: Partial<TodoItemType>) => void;
  onDelete: (itemId: string) => void;
  onAddSubtask: (itemId: string, input: { title: string }) => void;
  onToggleSubtask: (itemId: string, subtaskId: string) => void;
  onDeleteSubtask: (itemId: string, subtaskId: string) => void;
};

export const TodoItem = ({
  item,
  canEdit,
  onToggle,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
}: TodoItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(item.title);
  const [newSubtask, setNewSubtask] = useState('');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const overdue = !item.completed && isOverdue(item.dueDate);
  const dueToday = !item.completed && isDueToday(item.dueDate);
  const subtaskCount = item.subtasks.length;
  const subtaskCompleted = getSubtaskCompletedCount(item);

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== item.title) {
      onUpdate(item.id, { title: titleValue.trim() });
    } else {
      setTitleValue(item.title);
    }
    setEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(item.title);
      setEditingTitle(false);
    }
  };

  const handleAddSubtask = () => {
    if (newSubtask.trim()) {
      onAddSubtask(item.id, { title: newSubtask.trim() });
      setNewSubtask('');
    }
  };

  const handleSubtaskKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddSubtask();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: overdue ? brandColors.accent.red : 'divider',
        mb: 1,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: overdue ? brandColors.accent.red : brandColors.purple,
          boxShadow: 1,
        },
      }}
    >
      {/* Main row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          minHeight: 56,
        }}
      >
        {/* Drag handle */}
        {canEdit && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              color: 'text.disabled',
              display: 'flex',
              alignItems: 'center',
              mr: 0.5,
              touchAction: 'none',
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <DragIndicator fontSize="small" />
          </Box>
        )}

        {/* Checkbox */}
        <Checkbox
          checked={item.completed}
          onChange={() => onToggle(item.id)}
          disabled={!canEdit}
          sx={{
            color: item.priority ? PRIORITY_COLORS[item.priority] : brandColors.purple,
            '&.Mui-checked': {
              color: brandColors.accent.green,
            },
          }}
        />

        {/* Title */}
        <Box sx={{ flexGrow: 1, minWidth: 0, mx: 1 }}>
          {editingTitle && canEdit ? (
            <TextField
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
              autoFocus
              fullWidth
              size="small"
              variant="standard"
            />
          ) : (
            <Typography
              onClick={() => canEdit && setEditingTitle(true)}
              sx={{
                cursor: canEdit ? 'text' : 'default',
                textDecoration: item.completed ? 'line-through' : 'none',
                color: item.completed ? 'text.disabled' : 'text.primary',
                fontWeight: 500,
              }}
            >
              {item.title}
            </Typography>
          )}

          {/* Meta info */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {item.dueDate && (
              <Chip
                size="small"
                icon={<CalendarToday sx={{ fontSize: '14px !important' }} />}
                label={formatDueDate(item.dueDate)}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: overdue
                    ? alpha(brandColors.accent.red, 0.1)
                    : dueToday
                    ? alpha(brandColors.yellow, 0.2)
                    : alpha(brandColors.charcoal, 0.05),
                  color: overdue
                    ? brandColors.accent.red
                    : dueToday
                    ? brandColors.charcoal
                    : 'text.secondary',
                  '& .MuiChip-icon': {
                    color: 'inherit',
                  },
                }}
              />
            )}
            {subtaskCount > 0 && (
              <Chip
                size="small"
                label={`${subtaskCompleted}/${subtaskCount}`}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                }}
              />
            )}
            {item.priority && (
              <Chip
                size="small"
                label={item.priority}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: alpha(PRIORITY_COLORS[item.priority], 0.1),
                  color: PRIORITY_COLORS[item.priority],
                  textTransform: 'capitalize',
                }}
              />
            )}
            {item.isRecurring && (
              <Chip
                size="small"
                icon={<Repeat sx={{ fontSize: '14px !important' }} />}
                label="Repeats"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: alpha(brandColors.purple, 0.1),
                  color: brandColors.purple,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Expand/Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => onDelete(item.id)}
              sx={{ color: 'text.disabled', '&:hover': { color: brandColors.accent.red } }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Expanded details */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 1, bgcolor: alpha(brandColors.purple, 0.02) }}>
          {/* Description */}
          {(item.description || canEdit) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Description
              </Typography>
              <TextField
                value={item.description}
                onChange={(e) => canEdit && onUpdate(item.id, { description: e.target.value })}
                placeholder={canEdit ? 'Add a description...' : 'No description'}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                size="small"
                disabled={!canEdit}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          {/* Subtasks */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Subtasks
            </Typography>
            <Box sx={{ mt: 1 }}>
              {item.subtasks.map((subtask) => (
                <SubtaskRow
                  key={subtask.id}
                  subtask={subtask}
                  canEdit={canEdit}
                  onToggle={() => onToggleSubtask(item.id, subtask.id)}
                  onDelete={() => onDeleteSubtask(item.id, subtask.id)}
                />
              ))}
              {canEdit && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <TextField
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyDown={handleSubtaskKeyDown}
                    placeholder="Add subtask..."
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleAddSubtask}
                    disabled={!newSubtask.trim()}
                    sx={{
                      bgcolor: brandColors.purple,
                      color: 'white',
                      '&:hover': { bgcolor: brandColors.purpleDeep },
                      '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
